import Link from 'next/link';
import Image from 'next/image';
import { forwardRef, useEffect, useRef } from 'react';
import { gsap } from 'react-scale/lib/gsap';
import { useMedia } from 'react-use';

import { Container } from '../container';
import s from './footer.module.scss';
import { useDeviceDetect } from 'react-scale/hooks/use-device-detect';
import { useAppStore } from '../../context/use-app-store';
import { UseCanvas, ScrollScene } from '@14islands/r3f-scroll-rig';
import ScaleFooter3D from './3d';
import { OrthographicCamera } from '@react-three/drei';

export const Footer = () => {
  const zeitgeistRef = useRef<SVGSVGElement>();
  const dividerRef = useRef<HTMLHRElement>();
  const footerRef = useRef<HTMLElement>();
  const scrollTimeline = useRef<GSAPTimeline>(null);
  const isDesktopInPx = useMedia('(min-width: 1024px)');
  const isDesktop = useDeviceDetect();

  const canvasLoaded = useAppStore(({ canvasLoaded }) => canvasLoaded);

  useEffect(() => {
    if (!isDesktop || !isDesktopInPx || !canvasLoaded) return;

    const cleanUp = () => {
      if (!scrollTimeline.current) return;

      scrollTimeline.current.getChildren().map(t => {
        t.targets().forEach((target: HTMLElement) => gsap.set(target, { clearProps: 'all' }));
        t.kill();
      });

      scrollTimeline.current.scrollTrigger?.kill(true);
      scrollTimeline.current.kill();
    };

    const path = zeitgeistRef.current.querySelector('path');

    scrollTimeline.current = gsap
      .timeline({
        defaults: {
          ease: 'linear'
        },
        scrollTrigger: {
          scrub: 0,
          trigger: footerRef.current,
          start: 'center bottom',
          end: 'bottom bottom'
        }
      })
      .fromTo(
        path,
        { drawSVG: '0% 0%' },
        {
          duration: 78,
          drawSVG: '0% 13.9%'
        }
      )
      .to(
        path,
        {
          fillOpacity: 1,
          duration: 22
        },
        '>'
      )
      .from(
        dividerRef.current,
        {
          scaleX: 0,
          duration: 22
        },
        '<'
      );

    return cleanUp;
  }, [isDesktop, isDesktopInPx, canvasLoaded]);

  useEffect(() => {
    if (scrollTimeline.current) scrollTimeline.current.scrollTrigger.refresh();
  }, []);

  return (
    <>
      <footer className={s['footer']} ref={footerRef}>
        <Container>
          <div className="relative">
            <Image
              className="absolute z-0 top-7 -left-6 sm:hidden"
              src="/static/zeitgeist/2023/morph.png"
              quality={100}
              alt="Zeitgeist morph"
              width={130}
              height={130}
            />
            <Image
              className="absolute -right-10 -top-5 sm:hidden"
              src="/static/zeitgeist/2023/morph-1.png"
              quality={100}
              alt="Zeitgeist morph"
              width={179}
              height={179}
            />
            <Image
              className="absolute z-20 -bottom-9 -right-9 sm:hidden"
              src="/static/zeitgeist/2023/morph-2.png"
              quality={100}
              alt="Zeitgeist morph"
              width={175}
              height={175}
            />
            <Zeitgeist ref={zeitgeistRef} className={s['logo']} />
          </div>

          <div className={s['footer__top']}>
            <p className={s['footer__work-with-us']}>
              <Link className={s['footer-demo__link']} href="/demo">
                Work with us &#8599;
              </Link>
            </p>

            <div className={s['footer__bottom-links']}>
              <Link
                href="https://twitter.com/scale_AI"
                target="_blank"
                className={s['footer__link']}
              >
                Twitter
              </Link>
              <Link
                href="https://linkedin.com/company/scaleai"
                target="_blank"
                className={s['footer__link']}
              >
                LinkedIn
              </Link>
              <Link
                href="https://youtube.com/@Scale_AI"
                target="_blank"
                className={s['footer__link']}
              >
                Youtube
              </Link>
            </div>
          </div>

          <hr className={s['divider']} ref={dividerRef} />

          <div className={s['footer__bottom']}>
            <p>© 2023 Scale Inc All Rights Reserved.</p>
            <div className={s['footer__bottom-links']}>
              <Link href="/legal/terms" target="_blank" className={s['footer__link--common']}>
                Terms & Conditions
                <span className="hidden lg:inline">,</span>
              </Link>
              <Link href="/legal/privacy" target="_blank" className={s['footer__link--common']}>
                Privacy Policy
                <span className="hidden lg:inline">, </span>
              </Link>
              <Link href="/legal/cookie" target="_blank" className={s['footer__link--common']}>
                Cookie Policy
              </Link>
            </div>
          </div>
        </Container>
      </footer>
      {isDesktopInPx && (
        <UseCanvas>
          <ScrollScene track={footerRef}>
            {_props => (
              // @ts-ignore
              <OrthographicCamera position={[0, 0, 0]}>
                <ScaleFooter3D />
              </OrthographicCamera>
            )}
          </ScrollScene>
        </UseCanvas>
      )}
    </>
  );
};

const Zeitgeist = forwardRef<SVGSVGElement, { className?: string }>(({ className }, ref) => {
  return (
    <svg
      ref={ref}
      className={className}
      viewBox="0 0 1412 349"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={s.logo__stroke}
        fillRule="evenodd"
        clipRule="evenodd"
        d="m418.133 0-20.408 20.409 20.408 20.407L438.54 20.41 418.133 0Zm15.403 73.93H401.96s7.618 12.426 15.408 30.154c-8.001 17.843-15.97 41.416-15.97 64.535 0 23.217 8.037 48.643 16.073 68.263-7.826 19.385-15.511 33.424-15.511 33.424h31.576s-8.032-13.811-16.065-33.424c8.119-20.111 16.39-45.976 16.39-67.887 0-22.003-8.341-46.36-16.493-64.911 8.067-17.99 16.168-30.154 16.168-30.154Zm-254.112 98.189c0-32.756 19.729-51.482 42.198-61.712-4.463 13.714-5.925 28.877-7.143 41.516l-.017.175h117.44c-1.27-13.975-2.857-30.071-7.853-44.16 21.78 8.529 40.897 25.126 41.739 56.865 0 4.199-.381 8.78-.762 13.357l-.01.116H212.537v2.7c.571 18.414 6.569 40.899 16.981 58.965-31.346-7.376-50.094-25.91-50.094-67.822Zm134.192 70.976c-10.091 16.104-23.632 29.324-39.085 29.324-18.935 0-34.307-13.903-45.013-32.478 13.577 3.194 29.517 4.295 47.322 4.295 13.519 0 25.879-.044 36.776-1.141Zm0 0c8.012-12.785 13.85-27.388 16.747-38.636h31.956c-5.409 27.589-22.977 36.046-48.703 38.636Zm10.433-135.157c-7.118-20.075-21.157-36.078-51.058-36.078-30.708 0-44.546 17.58-51.369 38.547 18.47-8.409 38.791-11.077 51.525-11.077 12.818 0 32.782 1.512 50.902 8.608ZM5.004 73.93h156.332v23.875s-22.729 13.554-46.871 30.687c3.241-14.26 4.515-25.679 4.515-25.679H5.004V73.93ZM60.8 177.174c7.966-14.062 31.416-32.891 53.666-48.682-3.391 14.924-8.936 32.96-17.43 44.279-7.798 10.395-29.67 27.27-50.68 42.248 2.787-11.488 7.275-25.19 14.444-37.845Zm-14.443 37.845c-3.669 15.125-4.386 26.41-4.386 26.41h124.371v28.878H0v-23.876s22.627-14.495 46.356-31.412Zm444.921-112.207h-35.423V73.93h35.423V18.867h31.574V73.93h50.827v28.882h-50.827v138.371c-24.253-.635-31.574-4.97-31.574-33.12V102.812Zm31.574 138.371c4.427.116 9.418.109 15.032.101h.088l1.93-.003 2.588-.001 34.269.377v29.254H545.57c-16.941 0-22.719-6.162-22.719-22.717v-7.011Zm265.29-167.252h-27.338l-4.233 56.527c-.105-.525-3.784-6.034-11.471-12.242-11.255-25.88-29.85-46.605-52.837-46.605-21.007 0-38.307 16.857-49.775 39.096-23.961 10.079-47.64 28.623-47.64 61.414 0 41.009 24.317 59.496 51.061 67.48 11.364 19.06 27.34 32.82 46.354 32.82 21.117 0 38.527-17.311 49.946-39.869 5.579-4.257 10.26-9.757 14.359-16.749v43.721c0 21.237-5.514 43.378-16.116 60.282-13.501 2.017-30.016 2.442-49.726 2.442-3.923 0-7.754.024-11.485.047-10.432.065-20.087.125-28.807-.355-8.968-12.704-14.98-27.558-17.468-38.928h-32.342c3.516 30.295 21.855 37.388 49.81 38.928 10.471 14.831 24.971 26.729 42.6 26.729 20.729 0 36.683-11.746 47.418-28.863 32.657-4.88 47.69-19.077 47.69-59.123V73.931Zm-45.933 158.621c-12.241 9.339-28.804 12.692-53.024 12.692-12.638 0-28.336-1.182-43.276-5.643-11.879-19.923-18.718-45.638-18.718-67.48 0-19.653 5.536-42.484 15.297-61.414 19.933-8.385 40.062-10.912 46.919-10.912 26.921 0 45.107 9.872 55.693 18.421 7.599 17.472 11.852 37.293 11.852 54.673 0 18.953-5.34 41.087-14.743 59.663Zm82.409-60.433c0-32.756 19.73-51.482 42.199-61.712-4.464 13.714-5.925 28.877-7.144 41.515l-.016.176h117.439c-1.269-13.975-2.856-30.071-7.853-44.16 21.781 8.529 40.898 25.126 41.738 56.865 0 4.199-.38 8.78-.76 13.358v.001l-.01.114H857.731v2.7c.57 18.414 6.569 40.899 16.981 58.965-31.347-7.376-50.095-25.91-50.095-67.822Zm134.193 70.976c-10.092 16.104-23.632 29.324-39.085 29.324-18.936 0-34.308-13.903-45.013-32.478 13.577 3.194 29.517 4.295 47.321 4.295 13.52 0 25.88-.044 36.777-1.141Zm0 0c8.012-12.785 13.85-27.388 16.747-38.636h31.953c-5.41 27.589-22.975 36.046-48.7 38.636Zm10.432-135.157c-7.118-20.075-21.156-36.078-51.057-36.078-30.708 0-44.546 17.58-51.369 38.547 18.47-8.409 38.791-11.077 51.525-11.077 12.817 0 32.781 1.512 50.901 8.608ZM1063.33 0l-20.41 20.409 20.41 20.407 20.4-20.407L1063.33 0Zm15.4 73.93h-31.58s7.62 12.426 15.41 30.154c-8 17.843-15.97 41.416-15.97 64.535 0 23.217 8.04 48.643 16.08 68.263-7.83 19.385-15.52 33.424-15.52 33.424h31.58s-8.03-13.811-16.06-33.424c8.11-20.111 16.39-45.976 16.39-67.887 0-22.003-8.35-46.36-16.5-64.911 8.07-17.99 16.17-30.154 16.17-30.154Zm67.07 131.691c.58 11.664 6.07 28.577 15.57 42.466-23.56-4.865-47.45-16.218-47.92-42.466h32.35Zm89.58 43.41c-13.45 2.372-26.92 2.63-35.29 2.63-8.56 0-23.57-.446-38.72-3.574 9.33 13.621 22.51 24.334 38.72 24.334 14.54 0 26.86-10.651 35.29-23.39Zm-81.92-151.167c-18.36 4.547-35.77 13.812-35.77 32.669 0 40.816 35.04 49.672 78.93 55.062l.98.115 1.15.136c28.17 3.323 48.7 5.745 48.7 29.786 0 8.601-4.44 21.857-12.07 33.399 21.67-3.823 43.26-13.137 43.26-35.712 0-43.511-35.81-51.981-80.48-57.371-28.49-3.467-49.28-6.161-49.28-27.724 0-7.874.48-19.611 4.58-30.36Zm81.58-.29c-5.97-14.111-17.35-25.963-40.34-25.963-24.45 0-35.9 12.293-41.24 26.253 16.51-4.087 33.78-4.36 39.86-4.36h.64c6.66-.006 24.09-.02 41.08 4.07Zm0 0c4.68 11.054 6.03 23.493 7.03 32.712.13 1.164.25 2.276.37 3.328h32.35c-1.62-21.393-20.39-31.38-39.75-36.04Zm55.49 5.239h35.42v105.25c0 28.151 7.32 32.485 31.58 33.121v7.011c0 16.554 5.77 22.716 22.72 22.716h31.18v-29.253l-34.27-.378c-1.58 0-3.11.002-4.6.004-5.61.009-10.61.016-15.03-.1V102.813h50.82V73.93h-50.82V18.868h-31.58V73.93h-35.42v28.883Z"
        fill="url(#paint0_linear_744_12626)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_744_12626"
          x1="0"
          y1="-33.742"
          x2="1399.05"
          y2="617.988"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".216" stopColor="#fff" />
          <stop offset=".951" stopColor="#90969B" />
        </linearGradient>
      </defs>
    </svg>
  );
});
